import { useEffect, useState } from "react";
import { Alert, Box, Button, Typography } from "@mui/material";
import useCommon from "hooks/useCommon";
import { GetHousekeepingNotificationsAPI } from "api/pusher";
import colors from "assets/theme/base/colors";
import dayjs from "utils/dayjs";

function isRoomStatusNotification(notification, hotelId) {
  const payload = notification.payload || {};
  const notificationHotel = notification.hotelId || payload.hotelId;
  const notificationHotelId = notificationHotel?._id || notificationHotel;
  if (notificationHotelId && notificationHotelId !== hotelId) return false;

  const event = [
    notification.type,
    notification.event,
    notification.eventType,
    payload.type,
    payload.event,
    payload.eventType,
  ]
    .filter(Boolean)
    .join(" ");
  return (
    /(?:room|hk).*status|status[._ -]changed/i.test(event) ||
    payload.roomStatus != null ||
    payload.newStatus != null ||
    /\broom\b.*\bstatus\b/i.test(payload.message || "")
  );
}

export default function RoomStatusAlerts({ hotelId }) {
  const { STORE } = useCommon();
  const liveNotifications = STORE((state) => state.pusher.notifications);
  const [result, setResult] = useState({ hotelId: null, items: [], loading: false, error: null });
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let cancelled = false;
    if (!hotelId) return undefined;
    setResult({ hotelId, items: [], loading: true, error: null });

    async function load() {
      try {
        const matches = [];
        let page = 1;
        let hasMore = true;
        while (hasMore && matches.length < 5 && !cancelled) {
          const response = await GetHousekeepingNotificationsAPI({
            page,
            pageSize: 20,
            unreadOnly: false,
          });
          const data = response?.data?.data;
          const items = data?.items || [];
          matches.push(...items.filter((item) => isRoomStatusNotification(item, hotelId)));
          hasMore =
            items.length > 0 &&
            (data?.total != null ? page * (data.pageSize || 20) < data.total : items.length === 20);
          page += 1;
        }
        if (!cancelled) setResult({ hotelId, items: matches, loading: false, error: null });
      } catch (error) {
        if (!cancelled)
          setResult({
            hotelId,
            items: [],
            loading: false,
            error: "Unable to load room status updates.",
          });
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [hotelId, attempt]);

  const current = result.hotelId === hotelId;
  const items = Array.from(
    new Map(
      [
        ...(current ? result.items : []),
        ...liveNotifications.filter((item) => isRoomStatusNotification(item, hotelId)),
      ].map((item) => [item._id || item.id, item])
    ).values()
  )
    .sort((a, b) => (new Date(b.createdAt).getTime() || 0) - (new Date(a.createdAt).getTime() || 0))
    .slice(0, 5);

  return (
    <Box mb={2}>
      <Typography sx={{ fontSize: 12, fontWeight: 600, color: colors.primary.main, mb: 0.5 }}>
        Recent Room Status Updates
      </Typography>
      {!hotelId ? (
        <Typography sx={{ fontSize: 12 }}>Select a hotel to view room status updates.</Typography>
      ) : !current || result.loading ? (
        <Typography role="status" sx={{ fontSize: 12 }}>
          Loading room status updates...
        </Typography>
      ) : result.error ? (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => setAttempt((value) => value + 1)}>
              Retry
            </Button>
          }
        >
          {result.error}
        </Alert>
      ) : items.length === 0 ? (
        <Typography sx={{ fontSize: 12, opacity: 0.7 }}>No room status updates yet.</Typography>
      ) : (
        items.map((item) => (
          <Box
            key={item._id || item.id}
            sx={{
              px: 1,
              py: 0.75,
              mt: 1,
              borderRadius: "6px",
              bgcolor: "rgba(27,46,36,0.035)",
              borderLeft: `2px solid ${colors.primary.main}60`,
            }}
          >
            <Typography sx={{ fontSize: 11, lineHeight: 1.4, overflowWrap: "anywhere" }}>
              {item.payload?.message || "Room status updated"}
            </Typography>
            {item.createdAt && (
              <Typography
                component="time"
                dateTime={item.createdAt}
                sx={{ display: "block", mt: 0.25, fontSize: 9, lineHeight: 1.3, opacity: 0.6 }}
              >
                {dayjs(item.createdAt).format("MMM D · h:mm A")}
              </Typography>
            )}
          </Box>
        ))
      )}
    </Box>
  );
}
